// Hooks.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../../../../../../ui/toast';
import { IApplicantRequest } from '../../../../../../../interfaces';

// Function to handle recording save and status update

import {
  deleteInterviewApplicant,
  editInterviewApplicant,
  getApplicantCandidates,
  getInterviewApplicant,
  getInterviewApplicants,
  uploadInterviewAudio,
} from '../../../../../../../utils/api_service/SmartInterview';
import { ICandidate } from '../../../../../../../utils/types/JobOpenings';
import { getMenuItemsForMenu } from '../Functions/GetContextMenuItems';

interface IUseCandidate {
  interviewRoundID: string;
}

export const useCandidates = ({ interviewRoundID }: IUseCandidate) => {
  // — Search & filter state
  const saveRecordingAndUpdateStatus = async (
    audioBlob: Blob,
    candidate: ICandidate,
    interviewRoundID: string,
    onSuccess: () => void,
    uploadInterviewAudio: any,
    editInterviewApplicant: any,
  ) => {
    try {
      const audioFile = new File([audioBlob], `recording_${Date.now()}.wav`, {
        type: 'audio/wav',
      });

      const response = await uploadInterviewAudio(
        audioFile,
        candidate.interviewRoundId,
        candidate.id,
      );

      if (response.ok) {
        const updateResponse = await editInterviewApplicant(
          {
            interviewStartTime: String(candidate.interviewStartTime),
            interviewType: candidate.interviewType,
            meetingLink: candidate.meetingLink,
            inPersonAddress: candidate.inPersonAddress,
            interviewApplicantStatus: 'Completed',
          },
          candidate.id,
          interviewRoundID,
        );

        if (updateResponse.ok) {
          toast.success('Recording saved and status updated to Completed', {
            position: 'bottom-center',
          });
          onSuccess();
          return true;
        } else {
          toast.warning('Recording saved but failed to update status', {
            position: 'bottom-center',
          });
        }
      } else {
        toast.error('Failed to save recording.', { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('An error occurred while saving.', {
        position: 'bottom-center',
      });
    }
    return false;
  };
  const [searchObject, setSearchObject] = useState<IApplicantRequest>({
    email: '',
    name: '',
    interviewStatus: [],
  });
  const [filters, setFilters] = useState<{ [key: string]: any }>({});
  const [appliedFilters, setAppliedFilters] = useState<{ [key: string]: any }>(
    {},
  );

  // — Pagination / table state
  const [limit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [interviewApplicants, setInterviewApplicants] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [recordingConfirmOpen, setRecordingConfirmOpen] = useState(false);
  const [rowToStopRecording, setRowToStopRecording] =
    useState<ICandidate | null>(null);
  const [isOpenInvite, setIsOpenInvite] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<ICandidate | null>(null);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [isUpdateOpen, setUpdateOpen] = useState(false);
  const [meetingLink, setMeetingLink] = useState('');
  const [inPersonAddress, setInPersonAddress] = useState('');
  const [isVirtualType, setIsVirtualType] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingRowId, setRecordingRowId] = useState<string | null>(null);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [isPanelistNotesOpen, setPanelistNotesOpen] = useState(false);
  const [isRecordingDialogOpen, setIsRecordingDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const history = useHistory();

  // — Your existing API fetch, wrapped in useCallback

  const fetchCandidateApplicants = useCallback(async () => {
    if (!interviewRoundID) return;
    setIsLoading(true);

    try {
      // Get job opening id from route params
      const resp = await getApplicantCandidates(
        searchObject,
        pageNumber,
        limit,
        interviewRoundID,
      );

      const data = await resp.json();

      const applicantsWithNotes = await Promise.all(
        data.applicants.map(async (applicant: ICandidate) => {
          try {
            const res = await getInterviewApplicant(
              applicant.id,
              interviewRoundID,
            );
            const result = await res.json();
            const hasPanelistNotes = result?.panelistNotes?.length > 0;

            return {
              ...applicant,
              interviewRoundId: interviewRoundID, // make sure it's preserved
              hasPanelistNotes,
            };
          } catch (error) {
            console.error(
              `Error fetching Penelist notes for ${applicant.id}:`,
              error,
            );
            return {
              ...applicant,
              interviewRoundId: interviewRoundID,
              hasPanelistNotes: false,
            };
          }
        }),
      );

      setInterviewApplicants(applicantsWithNotes);
      setTotalRecords(data.total_count);
      setTotalPages(Math.ceil(data.total_count / limit));
    } catch (err) {
      toast.error('Error Getting Job Applicants', {
        position: 'bottom-center',
      });
    } finally {
      setIsLoading(false);
    }
  }, [interviewRoundID, searchObject, pageNumber, limit]);

  // — Initial + deps effect
  useEffect(() => {
    fetchCandidateApplicants();
  }, [fetchCandidateApplicants]);

  // — Handlers you already had
  const handleSearch = (q: string) => {
    setSearchObject((prev) => ({ ...prev, name: q, email: q }));
  };
  const onPageChange = (page: number) => setPageNumber(page);

  const handleApplyFilters = (selected: { [key: string]: any }) => {
    setFilters(selected);
    setAppliedFilters(selected);
  };
  const handleRemoveFilter = (key: string, value: any) => {
    setAppliedFilters((prev) => {
      const updated = { ...prev };
      updated[key] = updated[key]?.filter((v: any) => v !== value) ?? [];
      if (updated[key]?.length === 0) delete updated[key];
      return updated;
    });
    setFilters((prev) => {
      const updated = { ...prev };
      updated[key] = updated[key]?.filter((v: any) => v !== value) ?? [];
      if (updated[key]?.length === 0) delete updated[key];
      return updated;
    });
  };
  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
  };

  const openInvite = () => setIsOpenInvite(true);
  const closeInvite = () => setIsOpenInvite(false);
  const onInvitesSent = async () => {
    closeInvite();
    await fetchCandidateApplicants();
  };

  // Context Menu state

  // Refs & outside-click listener for closing menu + scroll lock
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const closeMenu = () => setAnchorEl(null);
  const openMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    row: ICandidate,
  ) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  useEffect(() => {
    // Save original body overflow
    const originalOverflow = document.body.style.overflow;

    // If menu open, disable scroll and attach outside-click
    if (anchorEl) {
      document.body.style.overflow = 'hidden';
      const handleClickOutside = (event: MouseEvent) => {
        const menuEl = menuContainerRef.current;
        if (
          menuEl &&
          !menuEl.contains(event.target as Node) &&
          !anchorEl.contains(event.target as Node)
        ) {
          closeMenu();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);

      // Cleanup on close/unmount
      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }

    // Restore scroll when menu is not open
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [anchorEl]);

  // DELETE dialog

  const onDeleteClick = (row: ICandidate) => {
    setSelectedRow(row);
    closeMenu();
    setDeleteOpen(true);
  };
  const handleConfirmDelete = async (row: ICandidate) => {
    try {
      const res = await deleteInterviewApplicant(row.id, interviewRoundID);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Delete failed');
      }
      toast.success('Candidate deleted', { position: 'bottom-center' });
      await fetchCandidateApplicants();
    } catch (e) {
      toast.error((e as Error).message, { position: 'bottom-center' });
    } finally {
      setDeleteOpen(false);
      setSelectedRow(null);
    }
  };

  // UPDATE dialog

  const onUpdateClick = (row: ICandidate) => {
    setSelectedRow(row);
    setMeetingLink(row.meetingLink || '');
    setInPersonAddress(row.inPersonAddress || '');
    setIsVirtualType(row.interviewType.trim().toLowerCase() === 'virtual');
    closeMenu();
    setUpdateOpen(true);
  };
  const handleApplyUpdate = async (row: ICandidate) => {
    try {
      const body = {
        interviewStartTime: String(row.interviewStartTime),
        interviewType: row.interviewType,
        meetingLink: isVirtualType ? meetingLink : undefined,
        inPersonAddress: !isVirtualType ? inPersonAddress : undefined,
        interviewApplicantStatus: row.interviewApplicantStatus,
      };
      const res = await editInterviewApplicant(body, row.id, interviewRoundID);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Update failed');
      }
      toast.success('Interview details updated', {
        position: 'bottom-center',
      });
      await fetchCandidateApplicants();
    } catch (e) {
      toast.error((e as Error).message, {
        position: 'bottom-center',
      });
    } finally {
      setUpdateOpen(false);
      setSelectedRow(null);
    }
  };

  // CONFIRM INTERVIEW dialog

  const onConfirmClick = (row: ICandidate) => {
    setIsVirtualType(row.interviewType.trim().toLowerCase() === 'virtual');
    setMeetingLink(row.meetingLink || '');
    setInPersonAddress(row.inPersonAddress || '');
    closeMenu();
    setConfirmOpen(true);
  };
  const handleConfirmInterview = async (row: ICandidate) => {
    try {
      const body = {
        interviewStartTime: String(row.interviewStartTime),
        interviewType: row.interviewType,
        interviewApplicantStatus: 'Confirmed' as const,
        // always a string
        meetingLink: isVirtualType ? row.meetingLink : '',
        inPersonAddress: !isVirtualType ? row.inPersonAddress : '',
      };

      const res = await editInterviewApplicant(body, row.id, interviewRoundID);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Confirm failed');
      }
      toast.success('Interview marked as confirmed', {
        position: 'bottom-center',
      });
      await fetchCandidateApplicants();
    } catch (e) {
      toast.error((e as Error).message, {
        position: 'bottom-center',
      });
    } finally {
      setConfirmOpen(false);
      setSelectedRow(null);
    }
  };

  // Build per-row menu items factory

  const onPanelistNoteClick = (row: ICandidate) => {
    setSelectedRow(row);
    setPanelistNotesOpen(true);
  };
  const openRecordClick = (row: ICandidate) => {
    setSelectedRow(row);
    setIsRecordingDialogOpen(true);
  };
  const closeRecordClick = () => {
    setIsRecordingDialogOpen(false);
    setSelectedRow(null);
  };
  const handleClosePanelistNotes = () => {
    setPanelistNotesOpen(false);
  };

  const handleSaveRecording = async (
    candidate: ICandidate,
    audioBlob: Blob,
  ) => {
    const success = await saveRecordingAndUpdateStatus(
      audioBlob,
      candidate,
      interviewRoundID,
      async () => {
        await fetchCandidateApplicants();
        closeRecordClick();
      },
      uploadInterviewAudio,
      editInterviewApplicant,
    );
    if (success) {
      setRecordingRowId(null);
    }
  };

  // Record Interview
  const onOpenPanelistNotes = (row: ICandidate) => {
    setSelectedRow(row);
    setPanelistNotesOpen(true);
  };

  const onRecordInterview = async (row: ICandidate) => {
    setSelectedRow(row);

    const isRecording = row.id === recordingRowId;

    if (isRecording) {
      setRowToStopRecording(row);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      if (!audioChunksRef.current) audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());

        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/wav',
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        const audioFile = new File([audioBlob], `recording_${Date.now()}.wav`, {
          type: 'audio/wav',
        });

        if (row.inPersonAddress) {
          try {
            const response = await uploadInterviewAudio(
              audioFile,
              row.interviewRoundId,
              row.id,
            );
            if (response.ok) {
              const body = {
                interviewStartTime: String(row.interviewStartTime),
                interviewType: row.interviewType,
                meetingLink: isVirtualType ? meetingLink : undefined,
                inPersonAddress: !isVirtualType ? inPersonAddress : undefined,
                interviewApplicantStatus: row.interviewApplicantStatus,
              };
              const updateResponse = await editInterviewApplicant(
                body,
                row.id,
                interviewRoundID,
              );
              if (updateResponse.ok) {
                toast.success(
                  'Recording saved and status updated to Completed',
                  { position: 'bottom-center' },
                );
                await fetchCandidateApplicants(); // Refresh the table data
              } else {
                toast.warning('Recording saved but failed to update status', {
                  position: 'bottom-center',
                });
              }
            } else {
              toast.error('Failed to save recording.', {
                position: 'bottom-center',
              });
            }
          } catch (error) {
            toast.error('An error occurred while recording.', {
              position: 'bottom-center',
            });
          }
        }

        // ✅ Set recordingRowId to null only here
        setRecordingRowId(null);

        audioChunksRef.current = [];
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      };

      mediaRecorder.start();
      setRecordingRowId(row.id);
    } catch (error) {
      toast.error('Please allow microphone access.', {
        position: 'bottom-center',
      });
    }
  };

  const onJoinMeeting = (row: ICandidate) => {
    setSelectedRow(row);

    const meetingLink = row.meetingLink ?? '';
    if (!meetingLink) {
      toast.error('Meeting link is undefined', { position: 'bottom-center' });
      return;
    }

    if (navigator.userAgent.includes('Brave')) {
      window.location.href = meetingLink;
    } else {
      window.open(meetingLink, '_blank');
    }
  };
  const makeMenuItemsFor = getMenuItemsForMenu({
    onDelete: onDeleteClick,
    onUpdate: onUpdateClick,
    onConfirmInterview: onConfirmClick,
    onPanelistNotes: onPanelistNoteClick,
    onRecordInterview: openRecordClick,
    onJoinMeeting: onJoinMeeting,
    recordingRowId: recordingRowId ?? undefined,
  });
  const handleRecordingSave = async (
    candidate: ICandidate,
    audioBlob: Blob,
  ) => {
    try {
      const audioFile = new File([audioBlob], `recording_${Date.now()}.wav`, {
        type: 'audio/wav',
      });

      const response = await uploadInterviewAudio(
        audioFile,
        candidate.interviewRoundId,
        candidate.id,
      );

      if (response.ok) {
        // Update candidate status to Completed
        const updateResponse = await editInterviewApplicant(
          {
            interviewStartTime: String(candidate.interviewStartTime),
            interviewType: candidate.interviewType,
            meetingLink: candidate.meetingLink,
            inPersonAddress: candidate.inPersonAddress,
            interviewApplicantStatus: 'Completed',
          },
          candidate.id,
          interviewRoundID,
        );

        if (updateResponse.ok) {
          toast.success('Recording saved and status updated to Completed', {
            position: 'bottom-center',
          });
          await fetchCandidateApplicants(); // Refresh the table data
          closeRecordClick(); // Close the dialog after successful save
        } else {
          toast.warning('Recording saved but failed to update status', {
            position: 'bottom-center',
          });
        }
      } else {
        toast.error('Failed to save recording.', {
          position: 'bottom-center',
        });
      }
    } catch (error) {
      toast.error('An error occurred while saving.', {
        position: 'bottom-center',
      });
    }
  };

  return {
    // table data
    interviewApplicants,
    totalPages,
    pageNumber,
    history,
    isLoading,
    totalRecords,

    handleSaveRecording,
    handleRecordingSave,
    // search & pagination
    searchQuery: searchObject.name,
    handleSearch,
    onPageChange,

    // filters
    filters,
    appliedFilters,
    handleApplyFilters,
    handleRemoveFilter,
    handleClearFilters,

    // **refresh** for after you send invites
    fetchCandidateApplicants,
    openInvite,
    openMenu,
    closeMenu,
    anchorEl,
    isOpenInvite,
    closeInvite,
    onInvitesSent,
    selectedRow,
    menuContainerRef,
    makeMenuItemsFor,
    isDeleteOpen,
    handleConfirmDelete,
    setDeleteOpen,
    setSelectedRow,
    isUpdateOpen,
    handleApplyUpdate,
    setUpdateOpen,
    isVirtualType,
    meetingLink,
    setMeetingLink,
    isConfirmOpen,
    inPersonAddress,
    setInPersonAddress,
    handleConfirmInterview,
    setConfirmOpen,
    isPanelistNotesOpen,
    handleClosePanelistNotes,
    onRecordInterview,
    onOpenPanelistNotes,
    recordingConfirmOpen,
    mediaRecorderRef,
    setRecordingRowId,
    setRecordingConfirmOpen,
    setRowToStopRecording,
    recordingRowId,
    openRecordClick,
    closeRecordClick,
    isRecordingDialogOpen,
  };
};
